import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
    ArrowLeft, Warning, User, Phone, Envelope, Buildings, MapPin,
    PaperPlaneTilt, ChatTeardropDots, ArrowsClockwise, UserSwitch,
    Briefcase, Handshake, Globe, ClipboardText, IdentificationCard,
    CheckCircle, XCircle,
} from "@phosphor-icons/react";
import { SLAPill } from "@/components/admin/SLAPill";
import { CommentRequiredDialog } from "@/components/admin/CommentRequiredDialog";
import { DemoSchedulingPanel } from "@/pages/coe/B2BLeadDetail";

/* ============================================================================
   AdminLeadDetail — unified per-lead command center
   ----------------------------------------------------------------------------
   Single page powering /admin/leads/:kind/:id for web | b2b | application.
   Sections:
       Header     — name + kind pill + status + SLA pill + assignee
       Details    — kind-specific facts (smile quiz answers, B2B form, app docs)
       Actions    — assign / reassign / change status (each requires comment)
       Comments   — pure-note thread (no status change)
       Timeline   — chronological audit trail of every event
   ========================================================================== */

const KIND_META = {
    web: { Icon: Globe, label: "Web lead", cls: "bg-sky-100 text-sky-700 border-sky-200" },
    b2b: { Icon: Handshake, label: "B2B lead", cls: "bg-violet-100 text-violet-700 border-violet-200" },
    application: { Icon: Briefcase, label: "Application", cls: "bg-amber-100 text-amber-800 border-amber-200" },
};

const WEB_STATUSES = ["new", "assigned", "contacted", "consulting", "converted", "not_interested", "invalid"];
const B2B_STATUSES = ["new", "contacted", "discovery_done", "qualified", "application_sent", "applied", "handed_to_admin", "won", "lost"];
const APP_STATUSES = ["pending", "in_review", "docs_requested", "approved", "rejected"];

const STATUSES_BY_KIND = {
    web: WEB_STATUSES,
    b2b: B2B_STATUSES,
    application: APP_STATUSES,
};

const TERMINAL = new Set([
    "converted", "not_interested", "invalid",
    "won", "lost",
    "approved", "rejected",
]);

const fmtDT = (s) => s ? new Date(s).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

const EVENT_LABEL = {
    assignment: "Assigned",
    reassignment: "Reassigned",
    status_change: "Status changed",
    comment: "Comment",
    sla_alert: "SLA alert",
};

export default function AdminLeadDetail() {
    const { kind, id } = useParams();
    const navigate = useNavigate();
    const [lead, setLead] = useState(null);
    const [events, setEvents] = useState([]);
    const [assignees, setAssignees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusDialog, setStatusDialog] = useState(null); // {target_status}
    const [assignDialog, setAssignDialog] = useState(null); // {mode:'assign'|'reassign', assignee_id}
    const [comment, setComment] = useState("");
    const [commenting, setCommenting] = useState(false);
    // iter98.99fa — Admin mirror of the PES Demo Scheduling Panel.
    // Only loaded when viewing a B2B lead. Fetched off the sales endpoint
    // (admin has access via the `_assert_can_view` role gate).
    const [b2bLead, setB2bLead] = useState(null);
    const [demoBusy, setDemoBusy] = useState(false);

    const load = () => {
        setLoading(true);
        Promise.all([
            api.get(`/admin/leads-hub/${kind}/${id}`),
            api.get(`/admin/leads-hub/${kind}/${id}/events`),
        ]).then(([l, e]) => {
            setLead(l.data);
            setEvents(e.data || []);
        }).catch(() => setLead(null)).finally(() => setLoading(false));
        // Pull the full b2b_lead doc (with demo_at + qualifying_answers)
        // alongside the leads-hub projection so we can render the demo panel.
        if (kind === "b2b") {
            api.get(`/sales/b2b-leads/${id}`).then((r) => setB2bLead(r.data)).catch(() => setB2bLead(null));
        } else {
            setB2bLead(null);
        }
    };
    useEffect(() => { load(); }, [kind, id]); // eslint-disable-line react-hooks/exhaustive-deps

    const reloadB2b = async () => {
        if (kind !== "b2b") return;
        try {
            const r = await api.get(`/sales/b2b-leads/${id}`);
            setB2bLead(r.data);
        } catch { /* swallow */ }
    };

    const submitDemo = async ({ demo_at, demo_format, agenda }) => {
        if (demoBusy) return;
        setDemoBusy(true);
        try {
            await api.post(`/sales/b2b-leads/${id}/schedule-demo`, { demo_at, demo_format, agenda });
            toast.success("Demo scheduled — calendar invite emailed");
            await reloadB2b();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to schedule demo");
        } finally {
            setDemoBusy(false);
        }
    };

    useEffect(() => {
        api.get("/admin/leads-hub/assignees").then((r) => setAssignees(r.data || []));
    }, []);

    if (loading && !lead) return <div className="p-6 text-muted-foreground text-sm">Loading lead…</div>;
    if (!lead) {
        return (
            <div className="bg-card border border-border rounded-2xl p-10 text-center">
                <Warning size={32} className="mx-auto mb-2 text-muted-foreground" />
                <h2 className="font-display text-xl font-bold mb-1">Lead not found.</h2>
                <Button onClick={() => navigate("/admin/leads")} variant="outline" className="rounded-full mt-3">Back to leads</Button>
            </div>
        );
    }

    const km = KIND_META[kind] || KIND_META.web;
    const isTerminal = TERMINAL.has(lead.status);

    const doAssign = async (commentText) => {
        const payload = {
            assignee_id: assignDialog.assignee_id,
            comment: commentText,
        };
        try {
            await api.post(`/admin/leads-hub/${kind}/${id}/assign`, payload);
            toast.success("Assignment recorded");
            setAssignDialog(null);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to assign");
            throw err;
        }
    };

    const doStatus = async (commentText) => {
        try {
            await api.post(`/admin/leads-hub/${kind}/${id}/status`, {
                status: statusDialog.target_status,
                comment: commentText,
            });
            toast.success("Status updated");
            setStatusDialog(null);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to update status");
            throw err;
        }
    };

    const addPureComment = async () => {
        if (comment.trim().length === 0) return;
        setCommenting(true);
        try {
            await api.post(`/admin/leads-hub/${kind}/${id}/comment`, { comment: comment.trim() });
            setComment("");
            load();
            toast.success("Comment added");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to add comment");
        }
        setCommenting(false);
    };

    return (
        <div className="space-y-5" data-testid="admin-lead-detail">
            <Link to="/admin/leads" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
                data-testid="lead-detail-back">
                <ArrowLeft size={14} /> Back to leads inbox
            </Link>

            {/* Header */}
            <div className="bg-card border border-border rounded-2xl p-5 sm:p-6">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex items-start gap-4 min-w-0 flex-1">
                        <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary grid place-items-center shrink-0">
                            <km.Icon size={24} weight="fill" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                                <h1 className="font-display text-2xl font-bold tracking-tight" data-testid="lead-detail-title">
                                    {lead.title}
                                </h1>
                                <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border font-bold ${km.cls}`}>
                                    {km.label}
                                </span>
                                <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border border-border bg-secondary text-foreground font-mono font-bold">
                                    {(lead.status || "").replace(/_/g, " ")}
                                </span>
                                <SLAPill sla={lead.sla} kind={kind} testid="lead-detail-sla" />
                            </div>
                            <div className="text-xs text-muted-foreground flex items-center gap-3 flex-wrap">
                                {lead.contact_name && <span className="flex items-center gap-1"><User size={11} /> {lead.contact_name}</span>}
                                {lead.phone && <span className="flex items-center gap-1"><Phone size={11} /> {lead.phone}</span>}
                                {lead.email && <span className="flex items-center gap-1"><Envelope size={11} /> {lead.email}</span>}
                                {lead.city && <span className="flex items-center gap-1"><MapPin size={11} /> {lead.city}</span>}
                                {lead.clinic && <span className="flex items-center gap-1"><Buildings size={11} /> {lead.clinic}</span>}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Quick metadata strip */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5 pt-5 border-t border-border">
                    <Meta label="Assigned to" value={lead.assignee_id ? `${lead.assignee_name} · ${lead.assignee_role || ""}` : "Unassigned"} />
                    <Meta label="Created" value={fmtDT(lead.created_at)} />
                    <Meta label="SLA deadline" value={lead.sla?.deadline_at ? fmtDT(lead.sla.deadline_at) : "—"} />
                    <Meta label="First touch" value={lead.first_touch_at ? fmtDT(lead.first_touch_at) : (lead.sla?.state === "not_assigned" ? "—" : "Pending")} />
                </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-5">
                {/* Left col — actions + details */}
                <div className="lg:col-span-2 space-y-5">
                    {/* Action bar */}
                    {!isTerminal && (
                        <div className="bg-card border border-border rounded-2xl p-5" data-testid="lead-actions-section">
                            <div className="text-[10px] font-bold tracking-wider uppercase text-muted-foreground mb-3">Actions</div>

                            {/* Admin fast-action bar — applications only.
                                Lets the admin approve or reject without first
                                routing to a counselor for assignment. Backend
                                already permits admin-role status changes
                                regardless of `hub_assignee_id`. */}
                            {kind === "application" && (
                                <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3 mb-3 flex flex-wrap items-center gap-2" data-testid="admin-fast-actions">
                                    <div className="flex-1 min-w-[180px]">
                                        <div className="text-xs font-bold text-amber-900">Admin fast action</div>
                                        <div className="text-[11px] text-amber-900/80">Approve or reject directly — counselor assignment is optional for admins.</div>
                                    </div>
                                    <Button
                                        size="sm"
                                        variant="default"
                                        className="rounded-full bg-emerald-600 hover:bg-emerald-700"
                                        onClick={() => setStatusDialog({ target_status: "approved" })}
                                        data-testid="admin-fast-approve"
                                    >
                                        <CheckCircle size={14} className="mr-1.5" weight="fill" /> Approve
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="destructive"
                                        className="rounded-full"
                                        onClick={() => setStatusDialog({ target_status: "rejected" })}
                                        data-testid="admin-fast-reject"
                                    >
                                        <XCircle size={14} className="mr-1.5" weight="fill" /> Reject
                                    </Button>
                                </div>
                            )}

                            <div className="grid sm:grid-cols-2 gap-3">
                                <AssignBox
                                    lead={lead}
                                    assignees={assignees}
                                    onAssign={(assigneeId) => setAssignDialog({
                                        mode: lead.assignee_id ? "reassign" : "assign",
                                        assignee_id: assigneeId,
                                    })}
                                />
                                <StatusBox
                                    lead={lead}
                                    kind={kind}
                                    onChange={(targetStatus) => setStatusDialog({ target_status: targetStatus })}
                                />
                            </div>
                        </div>
                    )}

                    {/* Kind-specific details */}
                    <DetailsCard lead={lead} kind={kind} />

                    {/* iter98.99fa — Admin mirror of the PES Demo Scheduling
                        panel. Loads when viewing a B2B lead so admins have
                        full feature parity (schedule / reschedule / view
                        calendar invite status). Hidden for terminal leads. */}
                    {kind === "b2b" && b2bLead && b2bLead.status !== "won" && b2bLead.status !== "lost" && (
                        <div data-testid="admin-b2b-demo-panel-wrapper">
                            <DemoSchedulingPanel
                                lead={b2bLead}
                                busy={demoBusy}
                                onSubmit={submitDemo}
                            />
                        </div>
                    )}

                    {/* Pure-comment box */}
                    <div className="bg-card border border-border rounded-2xl p-5" data-testid="lead-quick-comment">
                        <div className="text-[10px] font-bold tracking-wider uppercase text-muted-foreground mb-3">
                            Quick note (no status change)
                        </div>
                        <Textarea
                            rows={2}
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            placeholder="Drop a note that anyone seeing this lead will read…"
                            className="text-sm"
                            data-testid="lead-quick-comment-input"
                        />
                        <div className="flex justify-end mt-2">
                            <Button onClick={addPureComment} disabled={!comment.trim() || commenting} size="sm"
                                data-testid="lead-quick-comment-submit">
                                <ChatTeardropDots size={14} className="mr-1.5" /> Add comment
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Right col — timeline */}
                <div>
                    <TimelineCard events={events} />
                </div>
            </div>

            {/* Comment-required dialogs */}
            <CommentRequiredDialog
                open={!!statusDialog}
                onClose={() => setStatusDialog(null)}
                title={`Change status to "${(statusDialog?.target_status || "").replace(/_/g, " ")}"`}
                description="Required for accountability. Everyone working this lead will see your comment in the audit trail."
                ctaLabel="Confirm status change"
                ctaVariant={statusDialog && ["not_interested", "invalid", "lost", "rejected"].includes(statusDialog.target_status) ? "destructive" : "default"}
                onConfirm={doStatus}
            />
            <CommentRequiredDialog
                open={!!assignDialog}
                onClose={() => setAssignDialog(null)}
                title={assignDialog?.mode === "reassign" ? "Reassign this lead" : "Assign this lead"}
                description={assignDialog?.mode === "reassign"
                    ? "The current assignee and the new owner will both see this in the audit trail."
                    : "Once assigned, the SLA clock starts. The assignee owns first response."}
                ctaLabel={assignDialog?.mode === "reassign" ? "Confirm reassignment" : "Confirm assignment"}
                onConfirm={doAssign}
            />
        </div>
    );
}

function Meta({ label, value }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground mb-0.5">{label}</div>
            <div className="text-sm break-words">{value}</div>
        </div>
    );
}

/* ---------- Assign box ---------- */
function AssignBox({ lead, assignees, onAssign }) {
    const [pick, setPick] = useState("");
    const isReassign = !!lead.assignee_id;

    // Filter assignees by kind for sensible defaults
    const filtered = assignees.filter((a) => {
        if (lead.kind === "web") return ["counsellor", "support", "coe_lead", "admin"].includes(a.role);
        if (lead.kind === "b2b") return ["sales", "coe_lead", "admin"].includes(a.role);
        return ["counsellor", "sales", "coe_lead", "admin"].includes(a.role); // applications
    });

    return (
        <div className="rounded-xl bg-secondary/30 border border-border p-3">
            <div className="text-xs font-bold mb-2 flex items-center gap-1.5">
                <UserSwitch size={12} weight="fill" />
                {isReassign ? "Reassign owner" : "Assign owner"}
            </div>
            <select
                value={pick}
                onChange={(e) => setPick(e.target.value)}
                className="w-full h-9 rounded-lg border border-input bg-background px-2 text-sm"
                data-testid="lead-assign-select"
            >
                <option value="">Select an owner…</option>
                {filtered.map((a) => (
                    <option key={a.id} value={a.id}>{a.name} · {a.role}</option>
                ))}
            </select>
            <Button
                size="sm" className="w-full mt-2" disabled={!pick || pick === lead.assignee_id}
                onClick={() => onAssign(pick)}
                data-testid="lead-assign-btn"
            >
                {isReassign ? "Reassign" : "Assign"}
            </Button>
        </div>
    );
}

/* ---------- Status box ---------- */
function StatusBox({ lead, kind, onChange }) {
    const [target, setTarget] = useState("");
    const options = (STATUSES_BY_KIND[kind] || []).filter((s) => s !== lead.status);

    return (
        <div className="rounded-xl bg-secondary/30 border border-border p-3">
            <div className="text-xs font-bold mb-2 flex items-center gap-1.5">
                <ArrowsClockwise size={12} weight="fill" />
                Change status
            </div>
            <select
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                className="w-full h-9 rounded-lg border border-input bg-background px-2 text-sm"
                data-testid="lead-status-select"
            >
                <option value="">Move to…</option>
                {options.map((s) => (
                    <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
                ))}
            </select>
            <Button
                size="sm" className="w-full mt-2" disabled={!target}
                onClick={() => onChange(target)}
                data-testid="lead-status-btn"
            >
                Continue (requires comment)
            </Button>
        </div>
    );
}

/* ---------- Kind-specific details ---------- */
function DetailsCard({ lead, kind }) {
    return (
        <div className="bg-card border border-border rounded-2xl p-5">
            <div className="text-[10px] font-bold tracking-wider uppercase text-muted-foreground mb-3 flex items-center gap-1.5">
                <ClipboardText size={12} weight="duotone" />
                {kind === "web" ? "Smile quiz / web lead details" : kind === "b2b" ? "B2B intake details" : "Application credentials"}
            </div>

            {kind === "web" && (
                <dl className="grid sm:grid-cols-2 gap-3 text-sm">
                    <KV label="Source" value={lead.source} />
                    <KV label="Score" value={lead.score != null ? `${lead.score}/100` : "—"} />
                    {lead.message && <KV label="Message" value={lead.message} full />}
                    {Object.keys(lead.answers || {}).length > 0 && (
                        <KV label="Quiz answers" full value={
                            <ul className="space-y-1 mt-1">
                                {Object.entries(lead.answers).map(([k, v]) => (
                                    <li key={k} className="text-xs">
                                        <span className="text-muted-foreground">{k.replace(/_/g, " ")}:</span>{" "}
                                        <span className="font-medium">{String(v)}</span>
                                    </li>
                                ))}
                            </ul>
                        } />
                    )}
                </dl>
            )}

            {kind === "b2b" && (
                <dl className="grid sm:grid-cols-2 gap-3 text-sm">
                    <KV label="Clinic" value={lead.clinic} />
                    <KV label="Contact" value={lead.contact_name} />
                    <KV label="Role" value={lead.role} />
                    <KV label="Cases / month" value={lead.cases_per_month} />
                    <KV label="Current aligner brand" value={lead.current_aligner_brand} />
                    <KV label="City" value={lead.city} />
                    {lead.message && <KV label="Message" value={lead.message} full />}
                </dl>
            )}

            {kind === "application" && (
                <ApplicationDetailsBody lead={lead} />
            )}
        </div>
    );
}

/* iter97.51 — Application credentials card.
   Surfaces ALL fields captured on the apply form (~25 fields), grouped
   into sections so the reviewer can scan systematically. Reads from
   `lead.raw` (full provider_application doc) so nothing is hidden behind
   the leads-hub `_normalize` projection. Also wires a "Download PDF"
   button that opens the new admin endpoint in a new tab. */
function ApplicationDetailsBody({ lead }) {
    const raw = lead.raw || {};
    const downloadPdf = () => {
        const token = localStorage.getItem("ca_token") || "";
        const base = process.env.REACT_APP_BACKEND_URL || "";
        const url = `${base}/api/admin/applications/${raw.id || lead.id}/pdf?auth=${encodeURIComponent(token)}`;
        window.open(url, "_blank", "noopener");
    };
    const years = raw.years_in_practice ?? raw.years_experience;
    const yearsLabel = years != null && years !== ""
        ? `${years} year${Number(years) !== 1 ? "s" : ""}`
        : "";
    const prefFee = raw.preferred_consultation_fee;
    const prefFeeLabel = prefFee != null && prefFee !== ""
        ? `₹${Number(prefFee).toLocaleString("en-IN")}`
        : "";
    return (
        <div className="space-y-5">
            <div className="flex items-center justify-end -mt-1">
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={downloadPdf}
                    className="text-xs h-8 gap-1.5"
                    data-testid="application-download-pdf"
                >
                    <ClipboardText size={13} weight="duotone" />
                    Download PDF
                </Button>
            </div>
            <SectionGrid title="Identity & contact" rows={[
                ["Name", raw.name || lead.contact_name],
                ["Clinic", raw.clinic || lead.clinic],
                ["Email", raw.email || lead.email, "mono"],
                ["Phone", raw.phone || lead.phone, "mono"],
                ["City", raw.city || lead.city],
                ["Submitted via", (raw.source || "—").replace(/-/g, " ")],
            ]} />
            <SectionGrid title="Credentials" rows={[
                ["Degree", raw.degree || lead.degree],
                ["Specialty", raw.specialty || raw.specialization || lead.specialization],
                ["Years in practice", yearsLabel],
                ["Council reg #", raw.council_registration_number || lead.council_registration_number, "mono"],
                ["State council", raw.state_council || lead.state_dental_council],
                ["Board certifications", raw.board_certifications],
                ["LinkedIn", raw.linkedin_url, "link"],
                ["CV URL", raw.cv_url, "link"],
            ]} />
            <SectionGrid title="Practice details" rows={[
                ["Registered name", raw.clinic_registered_name],
                ["GST #", raw.gst_number, "mono"],
                ["Address", raw.address, "full"],
                ["Clinic photo URL", raw.clinic_photo_url, "link"],
                ["Cases / month", raw.cases_per_month],
                ["Current aligner brand", raw.current_aligner_brand],
            ]} />
            <SectionGrid title="Goals & expectations" rows={[
                ["Primary demographic", raw.primary_patient_demographic],
                ["Why Invisique™", raw.why_invisique, "full"],
                ["Preferred consultation fee", prefFeeLabel],
            ]} />
            <SectionGrid title="Compliance" rows={[
                ["Agreed to ToS", raw.agreed_to_tos ? "Yes" : "No"],
                ["Agreed at", fmtDT(raw.tos_agreed_at)],
                ["ToS version", raw.tos_version, "mono"],
            ]} />
            {raw.message && (
                <div data-testid="application-message-section">
                    <div className="text-[10px] font-bold tracking-[0.12em] uppercase text-primary mb-2 pb-1 border-b border-border">
                        Applicant message
                    </div>
                    <p className="text-sm bg-secondary/40 border-l-2 border-accent rounded-r p-3 whitespace-pre-wrap">
                        {raw.message}
                    </p>
                </div>
            )}
            {lead.internal_review_notes && (
                <div data-testid="application-internal-notes-section">
                    <div className="text-[10px] font-bold tracking-[0.12em] uppercase text-primary mb-2 pb-1 border-b border-border flex items-baseline justify-between gap-2">
                        <span>Internal review notes</span>
                        <span className="text-[9px] font-medium normal-case text-muted-foreground tracking-normal">
                            {raw.internal_review_notes_by ? `By ${raw.internal_review_notes_by}` : ""}
                            {raw.internal_review_notes_at ? ` · ${fmtDT(raw.internal_review_notes_at)}` : ""}
                        </span>
                    </div>
                    <p className="text-sm bg-amber-50 border-l-2 border-amber-300 rounded-r p-3 whitespace-pre-wrap">
                        {lead.internal_review_notes}
                    </p>
                </div>
            )}
        </div>
    );
}

/* Small helper — renders a key/value grid with empty fields dimmed.
   Variants: "mono" for codes, "link" for URLs (opens in new tab),
   "full" makes the value span both columns. */
function SectionGrid({ title, rows }) {
    return (
        <div>
            <div className="text-[10px] font-bold tracking-[0.12em] uppercase text-primary mb-2 pb-1 border-b border-border">
                {title}
            </div>
            <dl className="grid sm:grid-cols-2 gap-3 text-sm">
                {rows.map(([label, value, variant], i) => (
                    <KVRow key={i} label={label} value={value} variant={variant} />
                ))}
            </dl>
        </div>
    );
}

function KVRow({ label, value, variant }) {
    const full = variant === "full";
    const mono = variant === "mono";
    const isLink = variant === "link";
    const empty = value === undefined || value === null || value === "";
    return (
        <div className={full ? "sm:col-span-2" : ""}>
            <dt className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground mb-0.5">{label}</dt>
            <dd className={mono ? "text-sm font-mono break-all" : "text-sm break-words"}>
                {empty ? (
                    <span className="text-muted-foreground">—</span>
                ) : isLink ? (
                    <a
                        href={String(value).match(/^https?:/) ? value : `https://${value}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline break-all"
                    >
                        {value}
                    </a>
                ) : (
                    value
                )}
            </dd>
        </div>
    );
}

function KV({ label, value, full, mono }) {
    return (
        <div className={full ? "sm:col-span-2" : ""}>
            <dt className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground mb-0.5">{label}</dt>
            <dd className={mono ? "text-sm font-mono break-all" : "text-sm break-words"}>
                {value || <span className="text-muted-foreground">—</span>}
            </dd>
        </div>
    );
}

/* ---------- Timeline ---------- */
function TimelineCard({ events }) {
    return (
        <div className="bg-card border border-border rounded-2xl p-5 sticky top-4" data-testid="lead-timeline">
            <div className="text-[10px] font-bold tracking-wider uppercase text-muted-foreground mb-3">
                Audit trail ({events.length})
            </div>
            {events.length === 0 ? (
                <p className="text-xs text-muted-foreground" data-testid="lead-timeline-empty">
                    No events yet. Every status change, assignment, and comment will land here.
                </p>
            ) : (
                <ul className="space-y-3 max-h-[600px] overflow-y-auto pr-1">
                    {events.map((e) => (
                        <li key={e.id} className="flex gap-2.5" data-testid={`lead-event-${e.id}`}>
                            <div className="w-7 h-7 rounded-full bg-secondary grid place-items-center text-[10px] font-bold text-muted-foreground shrink-0">
                                {(e.actor_name || "?").slice(0, 1).toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-baseline justify-between gap-2 flex-wrap">
                                    <div className="text-xs font-semibold">
                                        {e.actor_name || "System"}
                                        <span className="ml-1.5 text-[10px] uppercase tracking-wide text-muted-foreground font-bold">
                                            {EVENT_LABEL[e.event_type] || e.event_type}
                                        </span>
                                    </div>
                                    <div className="text-[10px] text-muted-foreground tabular-nums">
                                        {fmtDT(e.created_at)}
                                    </div>
                                </div>
                                {e.event_type === "status_change" && (
                                    <div className="text-[11px] text-muted-foreground mt-0.5">
                                        <span className="font-mono">{(e.from_status || "—").replace(/_/g, " ")}</span>
                                        {" → "}
                                        <span className="font-mono font-medium">{(e.to_status || "—").replace(/_/g, " ")}</span>
                                    </div>
                                )}
                                {(e.event_type === "assignment" || e.event_type === "reassignment") && (
                                    <div className="text-[11px] text-muted-foreground mt-0.5">
                                        {e.from_assignee_name ? (
                                            <>From <span className="font-medium">{e.from_assignee_name}</span> → </>
                                        ) : null}
                                        <span className="font-medium">{e.to_assignee_name}</span>
                                    </div>
                                )}
                                {e.comment && (
                                    <p className="text-sm mt-1 bg-secondary/40 rounded p-2 whitespace-pre-wrap">
                                        {e.comment}
                                    </p>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
